import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';

import Global from '../store/global-selectors';
import { BillingPlanIds } from '../enums/billing-plan-ids';
import { useGlobalModalLaunchContext } from '../contexts/modal-context/global-modal-context';
import { Segment } from '../services/analytics-services/segment';
import { BUY_NOW_FLOW_DETECTED } from '../constants/analytics-events';
import { BuyNowService } from '../services/buy-now-service';
import { updateBuyNowSetting } from '../store/seller/seller.actions';
import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';
import { messages } from '../components/defer-buy-now-modal/messages';

export const useBuyNowDrawer = (): void => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const buyNowSetting = useSelector(Global.selectSellerBuyNowSetting);
  const shouldShowBuyNowFlow = buyNowSetting.optedInToBuyNow && !buyNowSetting.isBuyNowFlowCompleted;
  const modalContext = useGlobalModalLaunchContext();
  const { openManageSubscriptionDrawer, closeManageSubscriptionDrawer, openDeferBuyNowModal } = modalContext || {};
  const hasBuyNowBeenOpened = useRef(false);
  const subscriptionPlanInfo = useSelector(Global.selectSellerSubscriptionPlanInfo);

  // Use ref to avoid stale closure in onInterceptClose callback
  const subscriptionPlanInfoRef = useRef(subscriptionPlanInfo);
  useEffect(() => {
    subscriptionPlanInfoRef.current = subscriptionPlanInfo;
  }, [subscriptionPlanInfo]);

  const buyNowSettingRef = useRef(buyNowSetting);
  useEffect(() => {
    buyNowSettingRef.current = buyNowSetting;
  }, [buyNowSetting]);

  const hasPaidPlan = useHasPaidPlan(subscriptionPlanInfo?.billingPlanId);

  // Open drawer on mount if in buy now flow
  useEffect(() => {
    if (shouldShowBuyNowFlow && !hasBuyNowBeenOpened.current && openManageSubscriptionDrawer) {
      Segment.trackEvent(BUY_NOW_FLOW_DETECTED);

      openManageSubscriptionDrawer({
        onClose: async () => {
          try {
            const updatedSetting = await BuyNowService.markFlowCompleted(buyNowSettingRef.current);
            dispatch(updateBuyNowSetting(updatedSetting));
          } catch {
            NotificationService.showNotification({
              notificationType: NOTIFICATION_TYPE_ERROR,
              content: intl.formatMessage(messages.errorSavingProgress),
            });
          } finally {
            hasBuyNowBeenOpened.current = false;
            closeManageSubscriptionDrawer?.();
          }
        },
        onInterceptClose: () => {
          const currentPlanInfo = subscriptionPlanInfoRef.current;
          const billingPlanId = currentPlanInfo?.billingPlanId;
          const billingPlanIdNumber = billingPlanId ? parseInt(billingPlanId, 10) : null;
          const currentHasPaidPlan =
            billingPlanIdNumber !== null &&
            billingPlanIdNumber !== BillingPlanIds.Trial &&
            billingPlanIdNumber !== BillingPlanIds.Free2025Q2;

          if (currentHasPaidPlan) {
            return true;
          }
          openDeferBuyNowModal?.();
          return false;
        },
      });

      hasBuyNowBeenOpened.current = true;
    }
  }, [
    shouldShowBuyNowFlow,
    openManageSubscriptionDrawer,
    closeManageSubscriptionDrawer,
    openDeferBuyNowModal,
    hasPaidPlan,
    dispatch,
    intl,
  ]);
};

const useHasPaidPlan = (billingPlanId: string | undefined): boolean => {
  const billingPlanIdNumber = billingPlanId ? parseInt(billingPlanId, 10) : null;
  return billingPlanIdNumber !== null && billingPlanIdNumber !== BillingPlanIds.Trial && billingPlanIdNumber !== BillingPlanIds.Free2025Q2;
};
